import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, FileText, Loader2, Trash2, Sparkles, CheckCircle2 } from "lucide-react";
import AppShell from "../components/AppShell";
import { api, formatApiErrorDetail, fmtDateFull } from "../lib/api";
import { Button } from "../components/ui/button";

const BANKS = ["Monzo", "Starling", "Barclays", "HSBC", "Lloyds", "NatWest", "Nationwide", "Santander", "Halifax", "Revolut", "Chase"];

export default function UploadPage() {
    const navigate = useNavigate();
    const inputRef = useRef();
    const [drag, setDrag] = useState(false);
    const [busy, setBusy] = useState(false);
    const [stage, setStage] = useState("");
    const [statements, setStatements] = useState([]);
    const [last, setLast] = useState(null);

    const load = () => api.get("/statements").then((r) => setStatements(r.data));
    useEffect(() => { load(); }, []);

    const upload = async (file) => {
        if (!file) return;
        if (!/\.(csv|pdf|txt)$/i.test(file.name)) return toast.error("Please upload a CSV or PDF statement");
        setBusy(true);
        setStage(file.name.toLowerCase().endsWith(".pdf") ? "Reading PDF and extracting transactions with AI…" : "Reading CSV and categorising with AI…");
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/statements/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
            setLast(data);
            toast.success(`${data.tx_count} transactions imported${data.duplicates ? ` (${data.duplicates} duplicates skipped)` : ""}`);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
        } finally { setBusy(false); setStage(""); }
    };

    const demo = async () => {
        setBusy(true); setStage("Loading demo statement…");
        try {
            const { data } = await api.post("/statements/demo");
            setLast(data); toast.success("Demo statement loaded"); load();
        } catch (e) { toast.error("Could not load demo data"); } finally { setBusy(false); setStage(""); }
    };

    const remove = async (id) => {
        try { await api.delete(`/statements/${id}`); toast.success("Statement removed"); load(); } catch (e) { toast.error("Could not remove"); }
    };

    return (
        <AppShell title="Statements" subtitle="Add your data" testid="upload-page">
            <div className="grid gap-6 lg:grid-cols-5">
                <div className="lg:col-span-3">
                    <div onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
                        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); }}
                        onClick={() => !busy && inputRef.current?.click()} data-testid="upload-dropzone"
                        className={`flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed p-12 text-center transition-colors ${drag ? "border-primary bg-primary/10" : "border-border bg-card hover:border-primary/50"}`}>
                        <input ref={inputRef} type="file" accept=".csv,.pdf,.txt" className="hidden" data-testid="upload-file-input" onChange={(e) => upload(e.target.files?.[0])} />
                        {busy ? (
                            <><Loader2 className="h-10 w-10 animate-spin text-primary" /><p className="mt-4 text-sm font-medium text-foreground" data-testid="upload-stage">{stage}</p><p className="mt-1 text-xs text-muted-foreground">Usually 5–20 seconds</p></>
                        ) : (
                            <>
                                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/15"><Upload className="h-6 w-6 text-primary" /></div>
                                <p className="mt-4 font-heading text-lg font-bold text-foreground">Drop a bank statement here</p>
                                <p className="mt-1 text-sm text-muted-foreground">CSV or PDF · up to 10MB · we only read dates, descriptions and amounts</p>
                                <Button size="sm" className="mt-5 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" data-testid="upload-choose-btn">Choose file</Button>
                            </>
                        )}
                    </div>
                    {last && (
                        <div className="mt-4 flex items-start gap-3 rounded-2xl border border-primary/30 bg-primary/10 p-4" data-testid="upload-result">
                            <CheckCircle2 className="mt-0.5 h-5 w-5 text-primary" />
                            <div className="flex-1 text-sm">
                                <p className="font-semibold text-foreground">{last.tx_count} transactions imported from {last.filename}</p>
                                <p className="text-muted-foreground">{fmtDateFull(last.period_start)} → {fmtDateFull(last.period_end)}{last.duplicates ? ` · ${last.duplicates} duplicates skipped` : ""}</p>
                            </div>
                            <Button size="sm" onClick={() => navigate("/dashboard")} data-testid="upload-view-btn" className="bg-primary text-primary-foreground hover:bg-primary/90">See where it went</Button>
                        </div>
                    )}
                    <div className="mt-6 rounded-2xl border border-border bg-card p-5">
                        <p className="text-sm font-semibold text-foreground">Works with exports from</p>
                        <div className="mt-2 flex flex-wrap gap-1.5">{BANKS.map((b) => <span key={b} className="rounded-full bg-secondary px-2.5 py-1 text-xs text-muted-foreground">{b}</span>)}</div>
                        <p className="mt-3 text-xs text-muted-foreground">Tip: in your banking app, look for "Export" or "Download statement" and pick CSV. Upload several months for the best "vs usual" insights.</p>
                        <Button variant="outline" size="sm" onClick={demo} disabled={busy} data-testid="upload-demo-btn" className="mt-4 border-border bg-secondary text-foreground"><Sparkles className="mr-2 h-4 w-4" /> No statement handy? Load demo data</Button>
                    </div>
                </div>
                <section className="lg:col-span-2" data-testid="statements-list">
                    <h3 className="font-heading text-base font-bold text-foreground">Your statements</h3>
                    {statements.length === 0 ? <p className="mt-3 text-sm text-muted-foreground">Nothing uploaded yet.</p> : (
                        <ul className="mt-3 space-y-2">
                            {statements.map((s) => (
                                <li key={s.id} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3" data-testid={`statement-${s.id}`}>
                                    <FileText className="h-5 w-5 shrink-0 text-muted-foreground" />
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-medium text-foreground">{s.filename}</p>
                                        <p className="text-xs text-muted-foreground">{s.tx_count} txns · {fmtDateFull(s.period_start)} → {fmtDateFull(s.period_end)}</p>
                                    </div>
                                    <Button variant="ghost" size="icon" onClick={() => remove(s.id)} data-testid={`statement-delete-${s.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>
        </AppShell>
    );
}
